import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Trash2, Users as UsersIcon, Save, Loader2, ShieldAlert, ArrowLeft, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import CommonLoader from '../../components/CommonLoader';
import {
    getPlatformRoles, getPlatformPermissionCatalogue, createPlatformRole,
    updatePlatformRole, setPlatformRoleStatus, deletePlatformRole,
} from '../../redux/platformStaffSlice';

const MySwal = withReactContent(Swal);

// Permissions that should not be handed out casually: a role that can hire
// platform users or widen roles — including its own — is Super Admin with extra
// steps. Ticking them is allowed; doing so silently is not.
const SENSITIVE = new Set(['users.manage', 'roles.manage']);

const EMPTY_DRAFT = { platformRoleId: null, name: '', description: '', permissions: {}, status: 'active' };

// The PLATFORM role master — the Super Admin's "Roles" screen: what each kind
// of platform employee may do. The platform-level twin of the tenant app's
// UserRoles page, and deliberately the same design.
//
// The catalogue on the right is FETCHED, never bundled — the backend owns
// config/platformPermissions.js, so adding a permission there needs no
// frontend change at all.
export default function PlatformRoles() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { roles, rolesLoading, catalogue, catalogueLoading, mutating } = useSelector((s) => s.platformStaff);

    const [draft, setDraft] = useState(null);
    const [search, setSearch] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => { dispatch(getPlatformRoles({ limit: 100 })); }, [dispatch, refreshKey]);
    useEffect(() => { dispatch(getPlatformPermissionCatalogue()); }, [dispatch]);

    const refetch = () => setRefreshKey((k) => k + 1);

    const filteredRoles = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return roles || [];
        return (roles || []).filter((r) =>
            r.name?.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q));
    }, [roles, search]);

    const grantedCount = (perms) => Object.values(perms || {}).filter(Boolean).length;

    const openNew = () => setDraft({ ...EMPTY_DRAFT });
    const openEdit = (role) => setDraft({
        platformRoleId: role.platformRoleId,
        name: role.name || '',
        description: role.description || '',
        permissions: { ...(role.permissions || {}) },
        status: role.status || 'active',
    });

    // Write keys imply their read key (the API normalises the same way in
    // platformPermissionService, so the stored role and the live access agree):
    // ticking x.manage also ticks x.view; unticking x.view also drops x.manage.
    const IMPLIES = { 'tenants.manage': 'tenants.view', 'patients.manage': 'patients.view', 'masters.manage': 'masters.view', 'support.respond': 'support.view' };
    const toggle = (id) => setDraft((d) => {
        const next = { ...d.permissions };
        if (next[id]) {
            delete next[id];
            Object.entries(IMPLIES).forEach(([writeKey, readKey]) => { if (readKey === id) delete next[writeKey]; });
        } else {
            next[id] = true;
            if (IMPLIES[id]) next[IMPLIES[id]] = true;
        }
        return { ...d, permissions: next };
    });

    const toggleGroup = (group, on) => setDraft((d) => {
        const next = { ...d.permissions };
        group.permissions.forEach((p) => { if (on) next[p.id] = true; else delete next[p.id]; });
        return { ...d, permissions: next };
    });

    const save = async () => {
        const name = draft.name.trim();
        if (!name) { toast.warn(t('platformRoles.name_required', 'Give the role a name')); return; }
        if (grantedCount(draft.permissions) === 0) {
            // A role with nothing ticked produces an account that can sign in
            // and do nothing — which reads as a broken app, not an empty role.
            const proceed = await MySwal.fire({
                title: t('platformRoles.empty_title', 'This role grants nothing'),
                text: t('platformRoles.empty_text', 'Anyone holding it will be able to sign in but will see no screens. Save anyway?'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#e5e7eb',
                confirmButtonText: t('platformRoles.save_anyway', 'Save anyway'),
                cancelButtonText: t('platformRoles.cancel', 'Cancel'),
            });
            if (!proceed.isConfirmed) return;
        }

        const payload = {
            name,
            description: draft.description.trim() || null,
            permissions: Object.keys(draft.permissions).filter((k) => draft.permissions[k]),
        };

        try {
            if (draft.platformRoleId) {
                await dispatch(updatePlatformRole({ platformRoleId: draft.platformRoleId, data: payload })).unwrap();
                toast.success(t('platformRoles.updated', 'Role updated — everyone holding it is updated within seconds'));
            } else {
                await dispatch(createPlatformRole(payload)).unwrap();
                toast.success(t('platformRoles.created', 'Role created'));
            }
            setDraft(null);
            refetch();
        } catch (err) {
            toast.error(typeof err?.error === 'string' ? err.error : t('platformRoles.save_error', 'Failed to save the role'));
        }
    };

    const handleStatusToggle = async (role) => {
        const isActive = role.status === 'active';
        const next = isActive ? 'inactive' : 'active';
        if (isActive && role.staffCount > 0) {
            const proceed = await MySwal.fire({
                title: t('platformRoles.deactivate_title', 'Deactivate this role?'),
                text: t('platformRoles.deactivate_text', `${role.staffCount} user${role.staffCount === 1 ? '' : 's'} hold it. They will be blocked from signing in until it is switched back on.`),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#e5e7eb',
                confirmButtonText: t('platformRoles.deactivate', 'Deactivate'),
                cancelButtonText: t('platformRoles.cancel', 'Cancel'),
            });
            if (!proceed.isConfirmed) return;
        }
        try {
            await dispatch(setPlatformRoleStatus({ platformRoleId: role.platformRoleId, status: next })).unwrap();
            refetch();
        } catch (err) {
            toast.warn(typeof err?.error === 'string' ? err.error : t('platformRoles.status_error', 'Failed to update the status'));
        }
    };

    const handleDelete = async (role) => {
        // The server refuses this with 409 while anyone holds the role; saying
        // so before the click is friendlier than an error afterwards.
        if (role.staffCount > 0) {
            MySwal.fire({
                title: t('platformRoles.in_use_title', 'Still in use'),
                text: t('platformRoles.in_use_text', `${role.staffCount} user${role.staffCount === 1 ? '' : 's'} hold this role. Move them to another role first, or deactivate this one instead.`),
                icon: 'info',
                confirmButtonColor: '#4ABDBD',
            });
            return;
        }
        const result = await MySwal.fire({
            title: t('platformRoles.delete_title', 'Delete this role?'),
            text: t('platformRoles.delete_text', 'This cannot be undone.'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('platformRoles.delete', 'Delete'),
            cancelButtonText: t('platformRoles.cancel', 'Cancel'),
        });
        if (!result.isConfirmed) return;
        try {
            await dispatch(deletePlatformRole(role.platformRoleId)).unwrap();
            toast.success(t('platformRoles.deleted', 'Role deleted'));
            refetch();
        } catch (err) {
            toast.warn(typeof err?.error === 'string' ? err.error : t('platformRoles.delete_error', 'Failed to delete the role'));
        }
    };

    // ---------------------------------------------------------------- editor
    if (draft) {
        const total = grantedCount(draft.permissions);
        return (
            <div className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full" data-testid="platform-role-editor">
                <div className="flex items-center gap-3 mb-6">
                    <button onClick={() => setDraft(null)} data-testid="platform-role-editor-back"
                        className="p-2 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 cursor-pointer">
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                        {draft.platformRoleId ? t('platformRoles.edit_role', 'Edit role') : t('platformRoles.new_role', 'New role')}
                    </h1>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                    <div>
                        <label className="text-xs font-medium text-slate-500">{t('platformRoles.name', 'Role name')} *</label>
                        <input data-testid="platform-role-name" value={draft.name}
                            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                            placeholder={t('platformRoles.name_placeholder', 'e.g. Support Agent')}
                            className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]" />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="text-xs font-medium text-slate-500">{t('platformRoles.description', 'Description')}</label>
                        <input data-testid="platform-role-description" value={draft.description}
                            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                            placeholder={t('platformRoles.description_placeholder', 'What this person does')}
                            className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]" />
                    </div>
                </div>

                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-bold text-[#1a365d]">{t('platformRoles.permissions', 'Permissions')}</h2>
                    <span className="text-xs text-slate-400" data-testid="platform-role-permission-count">
                        {t('platformRoles.granted_count', { defaultValue: '{{count}} granted', count: total })}
                    </span>
                </div>

                {catalogueLoading ? (
                    <div className="flex justify-center py-10"><CommonLoader /></div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {catalogue.map((group) => {
                            const ids = group.permissions.map((p) => p.id);
                            const on = ids.filter((id) => draft.permissions[id]).length;
                            const allOn = on === ids.length && ids.length > 0;
                            return (
                                <div key={group.id} data-testid={`platform-permission-group-${group.id}`}
                                    className="border border-gray-200 rounded-xl overflow-hidden">
                                    <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-gray-50 to-blue-50/40 border-b border-gray-200">
                                        <div className="text-sm font-semibold text-[#1a365d]">
                                            {group.label}
                                            <span className="ml-2 text-[11px] font-normal text-slate-400">{on}/{ids.length}</span>
                                        </div>
                                        <button type="button" data-testid={`platform-permission-group-toggle-${group.id}`}
                                            onClick={() => toggleGroup(group, !allOn)}
                                            className="text-[11px] font-semibold text-[#2D8A8A] hover:underline cursor-pointer">
                                            {allOn ? t('platformRoles.clear_all', 'Clear all') : t('platformRoles.select_all', 'Select all')}
                                        </button>
                                    </div>
                                    <div className="divide-y divide-gray-100">
                                        {group.permissions.map((p) => {
                                            const checked = draft.permissions[p.id] === true;
                                            const sensitive = SENSITIVE.has(p.id);
                                            return (
                                                <label key={p.id} data-testid={`platform-permission-${p.id}`}
                                                    className="flex items-start gap-3 px-4 py-2.5 hover:bg-gray-50/60 cursor-pointer">
                                                    <input type="checkbox" checked={checked} onChange={() => toggle(p.id)}
                                                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#4ABDBD] focus:ring-[#4ABDBD] cursor-pointer" />
                                                    <span className="flex-1">
                                                        <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                                                            {p.label}
                                                            {sensitive && (
                                                                <ShieldAlert className="w-3.5 h-3.5 text-amber-500"
                                                                    title={t('platformRoles.sensitive', 'Sensitive — this role can change who may do what on the platform')} />
                                                            )}
                                                        </span>
                                                        <span className="block text-[11px] text-slate-400 leading-snug">{p.description}</span>
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-gray-100">
                    <button type="button" onClick={() => setDraft(null)}
                        className="px-4 py-2 text-sm rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-50 cursor-pointer">
                        {t('platformRoles.cancel', 'Cancel')}
                    </button>
                    <button type="button" onClick={save} disabled={mutating} data-testid="platform-role-save"
                        className="px-4 py-2 text-sm rounded-xl text-white font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] hover:shadow-lg disabled:opacity-60 flex items-center gap-2 cursor-pointer">
                        {mutating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {t('platformRoles.save', 'Save role')}
                    </button>
                </div>
            </div>
        );
    }

    // ------------------------------------------------------------------ list
    return (
        <div className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full" data-testid="platform-roles-page">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <div className="ml-2">
                    <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                        {t('platformRoles.title', 'Roles')}
                    </h1>
                    <p className="text-xs text-slate-400 mt-1">
                        {t('platformRoles.subtitle', 'Define what each member of the platform team may do. Assign one when you create a user.')}
                    </p>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                    <button onClick={() => navigate('/users')} data-testid="back-to-platform-users"
                        className="flex items-center gap-2 px-4 py-2 rounded-xl border border-[#4ABDBD] text-[#2D8A8A] font-semibold text-sm hover:bg-[#4ABDBD]/10 cursor-pointer whitespace-nowrap">
                        <UsersIcon className="w-4 h-4" />
                        {t('platformRoles.users', 'All users')}
                    </button>
                    <button onClick={openNew} data-testid="add-platform-role-button"
                        className="group flex items-center gap-2 w-full sm:w-auto cursor-pointer px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm whitespace-nowrap">
                        <Plus className="w-5 h-5" />
                        {t('platformRoles.add_role', 'New role')}
                    </button>
                </div>
            </div>

            <div className="relative w-full sm:w-72 mb-6">
                <input data-testid="platform-roles-search" value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('platformRoles.search_placeholder', 'Search roles…')}
                    className="w-full p-2 pl-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]" />
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
            </div>

            {rolesLoading ? (
                <div className="flex flex-col items-center justify-center py-10">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('platformRoles.loading', 'Loading...')}</span>
                </div>
            ) : filteredRoles.length === 0 ? (
                <div className="p-10 text-center text-gray-500 text-sm">
                    {t('platformRoles.no_roles', 'No roles yet. Create one to describe a job on your platform team.')}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filteredRoles.map((role) => {
                        const isActive = role.status === 'active';
                        return (
                            <div key={role.platformRoleId} data-testid={`platform-role-card-${role.platformRoleId}`}
                                className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow duration-300 flex flex-col">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <h3 className="text-sm font-bold text-[#1a365d] truncate">{role.name}</h3>
                                        <p className="text-[11px] text-slate-400 line-clamp-2 mt-0.5">{role.description || '—'}</p>
                                    </div>
                                    <button type="button" role="switch" aria-checked={isActive}
                                        data-testid={`platform-role-status-${role.platformRoleId}`}
                                        onClick={() => !mutating && handleStatusToggle(role)}
                                        disabled={mutating}
                                        title={isActive ? t('platformRoles.deactivate', 'Deactivate') : t('platformRoles.activate', 'Activate')}
                                        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-300 ${isActive ? 'bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A]' : 'bg-gray-300'} ${mutating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-300 ${isActive ? 'translate-x-6' : 'translate-x-1'}`} />
                                    </button>
                                </div>

                                <div className="flex items-center gap-3 mt-4 text-[11px] text-slate-500">
                                    <span className="inline-flex items-center gap-1">
                                        <UsersIcon className="w-3.5 h-3.5" />
                                        {t('platformRoles.holders', { defaultValue: '{{count}} user', count: role.staffCount || 0 })}
                                    </span>
                                    <span className="inline-flex items-center gap-1">
                                        {t('platformRoles.permissions_count', {
                                            defaultValue: '{{count}} permission',
                                            count: grantedCount(role.permissions),
                                        })}
                                    </span>
                                </div>

                                <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
                                    <button onClick={() => openEdit(role)} data-testid={`edit-platform-role-${role.platformRoleId}`}
                                        className="flex-1 px-3 py-1.5 text-xs rounded-lg border border-[#4ABDBD] text-[#2D8A8A] font-semibold hover:bg-[#4ABDBD]/10 cursor-pointer">
                                        {t('platformRoles.edit', 'Edit')}
                                    </button>
                                    <button onClick={() => handleDelete(role)} data-testid={`delete-platform-role-${role.platformRoleId}`}
                                        title={t('platformRoles.delete', 'Delete')}
                                        className="px-3 py-1.5 text-xs rounded-lg border border-red-200 text-red-500 hover:bg-red-50 cursor-pointer">
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
